import { useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { toast } from '@eureca/ui';
import { loadProgramDraft, saveProgramDraft } from '../program-create-draft';
import { AddBenefitDialog } from './AddBenefitDialog';
import { ProgramBenefitsPage as ProgramBenefitsPageComponent } from './ProgramBenefitsPage.component';
import { ADDITIONAL_PAYMENT_OPTIONS, BENEFIT_OPTIONS } from './ProgramBenefitsPage.mock';

type AddDialog = 'benefit' | 'additional-payment' | null;

export default function ProgramBenefitsPage() {
  const navigate = useNavigate();
  const [benefitOptions, setBenefitOptions] = useState<string[]>(() => [...BENEFIT_OPTIONS]);
  const [additionalPaymentOptions, setAdditionalPaymentOptions] = useState<string[]>(() => [
    ...ADDITIONAL_PAYMENT_OPTIONS,
  ]);
  const [selectedBenefits, setSelectedBenefits] = useState<string[]>([]);
  const [selectedAdditionalPayments, setSelectedAdditionalPayments] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeDialog, setActiveDialog] = useState<AddDialog>(null);

  // Restore selections from draft on mount
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    const draft = loadProgramDraft();
    if (draft.selectedBenefits?.length) {
      const customBenefits = draft.selectedBenefits.filter(
        (b) => !(BENEFIT_OPTIONS as readonly string[]).includes(b)
      );
      if (customBenefits.length) setBenefitOptions((prev) => [...prev, ...customBenefits]);
      setSelectedBenefits(draft.selectedBenefits);
    }
    if (draft.selectedAdditionalPayments?.length) {
      const customPayments = draft.selectedAdditionalPayments.filter(
        (p) => !(ADDITIONAL_PAYMENT_OPTIONS as readonly string[]).includes(p)
      );
      if (customPayments.length)
        setAdditionalPaymentOptions((prev) => [...prev, ...customPayments]);
      setSelectedAdditionalPayments(draft.selectedAdditionalPayments);
    }
  }, []);

  function toggle(list: string[], value: string) {
    return list.includes(value) ? list.filter((item) => item !== value) : [...list, value];
  }

  function addIfMissing(list: string[], value: string) {
    return list.includes(value) ? list : [...list, value];
  }

  function handleToggleBenefit(value: string) {
    setSelectedBenefits((prev) => toggle(prev, value));
  }

  function handleToggleAdditionalPayment(value: string) {
    setSelectedAdditionalPayments((prev) => toggle(prev, value));
  }

  function handleConfirmAddBenefit(value: string) {
    setBenefitOptions((prev) => addIfMissing(prev, value));
    setSelectedBenefits((prev) => addIfMissing(prev, value));
    setActiveDialog(null);
    toast('Benefício adicionado', {
      action: { label: 'Fechar', onClick: () => toast.dismiss() },
    });
  }

  function handleConfirmAddAdditionalPayment(value: string) {
    setAdditionalPaymentOptions((prev) => addIfMissing(prev, value));
    setSelectedAdditionalPayments((prev) => addIfMissing(prev, value));
    setActiveDialog(null);
    toast('Pagamento adicional adicionado', {
      action: { label: 'Fechar', onClick: () => toast.dismiss() },
    });
  }

  function handleBack() {
    void navigate({ to: '/programas/novo' });
  }

  function handleSubmit() {
    setIsSubmitting(true);
    saveProgramDraft({ selectedBenefits, selectedAdditionalPayments });
    setIsSubmitting(false);
    void navigate({ to: '/programas/novo/perguntas-frequentes' });
  }

  return (
    <>
      <ProgramBenefitsPageComponent
        benefitOptions={benefitOptions}
        selectedBenefits={selectedBenefits}
        onToggleBenefit={handleToggleBenefit}
        onAddBenefit={() => setActiveDialog('benefit')}
        additionalPaymentOptions={additionalPaymentOptions}
        selectedAdditionalPayments={selectedAdditionalPayments}
        onToggleAdditionalPayment={handleToggleAdditionalPayment}
        onAddAdditionalPayment={() => setActiveDialog('additional-payment')}
        progress={60}
        onBack={handleBack}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
      <AddBenefitDialog
        open={activeDialog === 'benefit'}
        onOpenChange={(open) => setActiveDialog(open ? 'benefit' : null)}
        title="Adicionar benefício"
        placeholder="Digite o nome do benefício"
        onAdd={handleConfirmAddBenefit}
      />
      <AddBenefitDialog
        open={activeDialog === 'additional-payment'}
        onOpenChange={(open) => setActiveDialog(open ? 'additional-payment' : null)}
        title="Adicionar pagamento adicional"
        placeholder="Digite o nome do pagamento adicional"
        onAdd={handleConfirmAddAdditionalPayment}
      />
    </>
  );
}
